import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_THREE_PL_SYSTEMS_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type EnableThreePLSystemsAppInput = {
  appType: AppTypeEnum
  baseURL: string
  clientID: string
  clientSecret: string
}

const formSchema = z.object({
  baseURL: z.string().min(1, 'Base URL is required'),
  clientID: z.string().min(1, 'Client ID is required'),
  clientSecret: z.string().min(1, 'Client Secret is required')
})

/**
 * This is the modal for the 3PL Systems TMS app.
 */
export function ThreePLSystemsModal({ isOpen, onClose, appType, app }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      baseURL: '',
      clientID: '',
      clientSecret: ''
    },
    resolver: zodResolver(formSchema)
  })

  const [enableThreePLSystemsApp, { loading }] = useMutation<
    { enableThreePLSystemsApp: { appEnabled: boolean } },
    EnableThreePLSystemsAppInput
  >(ENABLE_THREE_PL_SYSTEMS_APP, {
    context: {
      headers: {
        'ws-id': workspaceID
      }
    },
    onCompleted: () => {
      toast.success('3PL Systems app enabled successfully!')
      handleClose()
    },
    onError: (err) => {
      const errorMessage =
        err.message === 'Invalid client credentials'
          ? 'Invalid credentials. Please check your Client ID and Client Secret.'
          : `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`

      setFormError(errorMessage)
      toast.error(errorMessage)
    },
    refetchQueries: [GET_APPS]
  })

  const handleClose = () => {
    setFormError('')
    form.reset()
    onClose()
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    enableThreePLSystemsApp({
      variables: {
        appType: appType as AppTypeEnum,
        baseURL: values.baseURL,
        clientID: values.clientID,
        clientSecret: values.clientSecret
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable 3PL Systems for Chain</DialogTitle>
          <DialogDescription>
            Enter your 3PL Systems credentials to connect your TMS. You can find these in your 3PL Systems admin
            settings.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="baseURL"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Base URL</FormLabel>
                  <FormControl>
                    <Input placeholder="yourcompany.3plsystems.com" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="clientID"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client ID</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your Client ID" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="clientSecret"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client Secret</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Enter your Client Secret" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              {!!app.docs && (
                <a
                  href={app.docs}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                >
                  See detailed steps here
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                  {loading ? 'Enabling...' : 'Enable'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
